import type { HotelBoardCategory } from '@bandido/shared/types/hotels/hotel-search-filters';
import type { HotelCancellationState } from '@bandido/shared/types/hotels/hotel-cancellation';
import { HotelBoardLabel, HotelCancellationLabel } from './HotelConditionLabel';
import { HotelCancellation, HotelCancellationDetails, cancellationBoundary } from './HotelCancellation';
import { useEffect, useId, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  MenuItem,
  TextField,
  Stack,
  Typography,
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { groupHotelFares, HotelFare, HotelSummary, HotelSurcharge, hotelFareIsPackage } from './searchModel';
import { accountCanBook, useHotelBookingPermissions } from './useHotelBookingPermissions';
import { hotelFareCanReserve } from './hotelReservationFlow';

export function SurchargeLines({ surcharges, roomIndex }: { surcharges: HotelSurcharge[]; roomIndex?: number }) {
  const { t } = useTranslation();
  return (
    <>
      {surcharges.map((s, i) => (
        <Typography key={i} variant="body2" color={s.included === true ? 'text.secondary' : 'warning.main'}>
          {roomIndex === undefined ? '' : `${t('hotelSearch.roomCharge', { index: roomIndex + 1 })} · `}
          {s.description ?? s.charge_type ?? t('hotelSearch.surcharge')}
          {s.price ? ` · ${s.price.currency} ${s.price.amount}` : ''}
          {' · '}
          {t(
            s.included === true
              ? 'hotelSearch.chargeIncluded'
              : s.included === false
                ? 'hotelSearch.chargeExcluded'
                : 'hotelSearch.chargeUnknown',
          )}
        </Typography>
      ))}
    </>
  );
}

export interface HotelOfferFilters {
  provider: string;
  board: string;
  cancellation: string;
}
export function filterHotelOffers(fares: HotelFare[], filters: HotelOfferFilters, now: number): HotelFare[] {
  return fares.filter((fare) => {
    const boundary = cancellationBoundary(fare.cancellation);
    const state = boundary !== null && now >= boundary ? 'unknown' : (fare.cancellation?.state ?? 'unknown');
    return (
      (filters.provider === 'all' || (fare.connector_lineage.supplier_code ?? 'unknown') === filters.provider) &&
      (filters.board === 'all' ||
        (fare.room_stays.length > 0 &&
          fare.room_stays.every((room) => (room.board?.category ?? 'unknown') === filters.board))) &&
      (filters.cancellation === 'all' || state === filters.cancellation)
    );
  });
}
export function HotelResults({
  fares,
  hotels,
  onReserve,
  expanded = false,
  direct = false,
}: {
  expanded?: boolean;
  direct?: boolean;
  fares: HotelFare[];
  hotels: HotelSummary[];
  onReserve?: (fare: HotelFare) => void;
}) {
  const { t, i18n } = useTranslation();
  const permissions = useHotelBookingPermissions();
  const canReserve = (fare: HotelFare) =>
    hotelFareCanReserve(fare, !permissions.isError && accountCanBook(permissions.data, fare.connector_account_id));
  const id = useId();
  const [filters, setFilters] = useState<HotelOfferFilters>({ provider: 'all', board: 'all', cancellation: 'all' });
  const [clock, setClock] = useState(0);
  const [openPolicies, setOpenPolicies] = useState<Record<string, boolean>>({});
  const now = Date.now();
  useEffect(() => {
    const future = fares
      .map((f) => cancellationBoundary(f.cancellation))
      .filter((v): v is number => v !== null && v > Date.now());
    if (!future.length) return;
    const timer = setTimeout(() => setClock((v) => v + 1), Math.min(Math.min(...future) - Date.now(), 2147483647));
    return () => clearTimeout(timer);
  }, [fares, clock]);
  const groups = useMemo(() => groupHotelFares(fares, true), [fares]);
  const names = new Map(hotels.map((h) => [h.hotel_id, h]));
  const providers = [...new Set(fares.map((f) => f.connector_lineage.supplier_code ?? 'unknown'))].sort();
  const boards = [...new Set(fares.flatMap((f) => f.room_stays.map((r) => r.board?.category ?? 'unknown')))].sort();
  const field = (key: keyof HotelOfferFilters, label: string, choices: string[], name: (value: string) => string) => (
    <TextField
      select
      size="small"
      id={`${id}-${key}`}
      label={label}
      value={filters[key]}
      onChange={(event) => setFilters((current) => ({ ...current, [key]: event.target.value }))}
      sx={{ flex: 1, minWidth: 140 }}
    >
      <MenuItem value="all">{t('hotelSearch.allDetailFilters')}</MenuItem>
      {[...new Set([...choices, ...(filters[key] !== 'all' ? [filters[key]] : [])])].map((value) => (
        <MenuItem key={value} value={value}>
          {key === 'board' ? (
            <HotelBoardLabel category={value as HotelBoardCategory} />
          ) : key === 'cancellation' ? (
            <HotelCancellationLabel state={value as HotelCancellationState} />
          ) : (
            name(value)
          )}
        </MenuItem>
      ))}
    </TextField>
  );
  return (
    <Box>
      {direct && (
        <Stack gap={1} mb={2}>
          <Stack direction="row" gap={1.5} flexWrap="wrap">
            {field('provider', t('hotelSearch.providerLabel'), providers, (value) =>
              value === 'unknown' ? t('hotelSearch.unknownBroker') : value,
            )}
            {field('board', t('hotelSearch.boardFilter'), boards, (value) => t(`hotelSearch.boards.${value}`))}
            {field(
              'cancellation',
              t('hotelSearch.cancellationLabel'),
              ['free_now', 'penalty_applies', 'non_refundable', 'unknown'],
              (value) => t(`hotelSearch.cancellationState.${value}`),
            )}
          </Stack>
          <Typography variant="caption">{t('hotelSearch.loadedOfferFilters')}</Typography>
        </Stack>
      )}
      <Alert severity="warning">{t('hotelSearch.priceWarning')}</Alert>
      <Typography variant="caption" display="block" sx={{ my: 1 }}>
        {t('hotelSearch.wholeStay')}
      </Typography>
      <Stack spacing={2}>
        {groups.map(({ hotelId, offers }) => {
          const hotel = names.get(hotelId);
          const visible = direct ? filterHotelOffers(offers, filters, now) : offers;
          return (
            <Accordion
              expanded={direct ? true : undefined}
              defaultExpanded={expanded}
              key={hotelId}
              TransitionProps={{ unmountOnExit: true }}
            >
              <AccordionSummary sx={direct ? { display: 'none' } : undefined} expandIcon={<ExpandMore />}>
                <Box>
                  <Typography variant="h6">{hotel?.display_name ?? hotelId}</Typography>
                  <Typography variant="body2">{t('hotelSearch.offerCount', { count: offers.length })}</Typography>
                  {hotel?.distance_km != null && (
                    <Typography variant="caption">
                      {new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 1 }).format(hotel.distance_km)} km
                    </Typography>
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ px: { xs: 0, md: 1 } }}>
                {!visible.length && <Typography role="status">{t('hotelSearch.noLoadedOffers')}</Typography>}
                <Box
                  component="table"
                  aria-label={t('hotelSearch.completeOffers')}
                  sx={{
                    width: '100%',
                    tableLayout: 'fixed',
                    borderCollapse: 'separate',
                    borderSpacing: { xs: 0, md: '0 12px' },
                    display: { xs: 'block', md: 'table' },
                    '& > tbody': { display: { xs: 'block', md: 'table-row-group' } },
                    '& th': { textAlign: 'left', p: 1, bgcolor: 'action.hover', fontSize: 12, fontWeight: 500 },
                    '& td': { p: { xs: 0, md: 1 }, verticalAlign: 'top', overflowWrap: 'anywhere' },
                  }}
                >
                  <Box component="thead" sx={{ display: { xs: 'none', md: 'table-header-group' } }}>
                    <tr>
                      <th style={{ width: '27%' }}>{t('hotelSearch.roomsLabel')}</th>
                      <th style={{ width: '16%' }}>{t('hotelSearch.boardFilter')}</th>
                      <th style={{ width: '27%' }}>{t('hotelSearch.cancellationLabel')}</th>
                      <th style={{ width: '12%' }}>{t('hotelSearch.providerLabel')}</th>
                      <th style={{ width: '18%', textAlign: 'right' }}>{t('hotelSearch.priceLabel')}</th>
                    </tr>
                  </Box>
                  {visible.map((offer) => (
                    <Box
                      component="tbody"
                      key={`${offer.search_id}:${offer.fare_id}`}
                      sx={{
                        outline: { xs: 'none', md: '1px solid' },
                        outlineColor: (theme) => theme.palette.divider,
                        borderRadius: 2,
                        bgcolor: 'background.paper',
                      }}
                    >
                      <Box
                        component="tr"
                        data-hotel-fare={`${offer.search_id}:${offer.fare_id}`}
                        key={`${offer.search_id}:${offer.fare_id}`}
                        sx={{
                          display: { xs: 'grid', md: 'table-row' },
                          gridTemplateColumns: 'minmax(0,1fr) minmax(0,1.4fr)',
                          gap: 2,
                          border: { xs: 1, md: 0 },
                          borderBottom: 1,
                          borderColor: 'divider',
                          borderRadius: 2,
                          p: { xs: 2, md: 0 },
                          mt: { xs: 2, md: 0 },
                        }}
                      >
                        <Box component="td" sx={{ gridColumn: '1 / -1', gridRow: 2 }}>
                          <Stack
                            direction={{ xs: 'row', md: 'column' }}
                            gap={1.5}
                            sx={{
                              bgcolor: { xs: 'action.hover', md: 'transparent' },
                              p: { xs: 1, md: 0 },
                              borderRadius: 1,
                              flexWrap: 'wrap',
                            }}
                          >
                            {offer.room_stays.map((room) => (
                              <Box key={room.room_index} sx={{ flex: 1, minWidth: 100 }}>
                                <Typography variant="body2" fontWeight={500}>
                                  {t('hotelSearch.roomLabel', { index: room.room_index + 1 })}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                                  {room.room_name ?? '—'}
                                </Typography>
                                <Typography variant="caption">
                                  {t(room.adults === 1 ? 'hotelSearch.occupancyAdult' : 'hotelSearch.occupancyAdults', {
                                    count: room.adults,
                                  })}
                                  {room.children_ages.length > 0
                                    ? ` · ${t('hotelSearch.occupancyChildren', { ages: room.children_ages.join(', ') })}`
                                    : ''}
                                </Typography>
                                {room.rate_name && (
                                  <Typography variant="caption" display="block">
                                    {room.rate_name}
                                  </Typography>
                                )}
                              </Box>
                            ))}
                          </Stack>
                        </Box>
                        <Box component="td" sx={{ gridColumn: 1, gridRow: 3 }}>
                          <Typography variant="caption" sx={{ display: { xs: 'block', md: 'none' } }}>
                            {t('hotelSearch.boardFilter')}
                          </Typography>
                          {offer.board_mixed ? (
                            offer.room_stays.map((room) => (
                              <Typography component="div" key={room.room_index} variant="body2">
                                {t('hotelSearch.roomLabel', { index: room.room_index + 1 })} ·{' '}
                                <HotelBoardLabel category={room.board?.category ?? 'unknown'} />
                              </Typography>
                            ))
                          ) : (
                            <HotelBoardLabel category={offer.room_stays[0]?.board?.category ?? 'unknown'} />
                          )}
                          {offer.board_mixed && (
                            <Typography variant="caption">{t('hotelSearch.mixedBoards')}</Typography>
                          )}
                        </Box>
                        <Box component="td" sx={{ gridColumn: 2, gridRow: 3 }}>
                          <Typography variant="caption" sx={{ display: { xs: 'block', md: 'none' } }}>
                            {t('hotelSearch.cancellationLabel')}
                          </Typography>
                          <HotelCancellation
                            policy={offer.cancellation}
                            renderDetails={false}
                            detailsOpen={!!openPolicies[`${offer.search_id}:${offer.fare_id}`]}
                            onDetailsToggle={() =>
                              setOpenPolicies((current) => ({
                                ...current,
                                [`${offer.search_id}:${offer.fare_id}`]:
                                  !current[`${offer.search_id}:${offer.fare_id}`],
                              }))
                            }
                          />
                        </Box>
                        <Box component="td" sx={{ gridColumn: '1 / -1', gridRow: 1 }}>
                          <Stack gap={0.5} direction={{ xs: 'row', md: 'column' }} alignItems="flex-start">
                            <Chip
                              size="small"
                              label={offer.connector_lineage.supplier_code ?? t('hotelSearch.unknownBroker')}
                            />
                            {hotelFareIsPackage(offer) && <Chip size="small" color="warning" label="PACKAGE" />}
                          </Stack>
                        </Box>
                        <Box component="td" sx={{ gridColumn: '1 / -1', gridRow: 4 }}>
                          <Stack
                            direction={{ xs: 'row', md: 'column' }}
                            justifyContent="space-between"
                            alignItems={{ xs: 'center', md: 'flex-end' }}
                            gap={1}
                            flexWrap="wrap"
                          >
                            <Typography variant="subtitle1" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                              {offer.total_price.currency} {offer.total_price.amount}
                            </Typography>
                            {onReserve && (
                              <Button
                                disabled={!canReserve(offer)}
                                onClick={() => onReserve(offer)}
                                sx={{ minHeight: 44 }}
                              >
                                {t(
                                  `hotelReservation.${
                                    canReserve(offer)
                                      ? 'quote'
                                      : permissions.isError
                                        ? 'permissionError'
                                        : permissions.isLoading
                                          ? 'permissionLoading'
                                          : !accountCanBook(permissions.data, offer.connector_account_id)
                                            ? 'bookingsDisabledShort'
                                            : 'fareIncompleteShort'
                                  }`,
                                )}
                              </Button>
                            )}
                          </Stack>
                          {offer.connector_lineage.rate_rules.filter((rule) => rule !== 'PACKAGE').length > 0 && (
                            <Typography variant="caption">
                              {t('hotelSearch.restrictions')}:{' '}
                              {offer.connector_lineage.rate_rules.filter((rule) => rule !== 'PACKAGE').join(', ')}
                            </Typography>
                          )}
                          <SurchargeLines surcharges={offer.connector_lineage.surcharges} />
                          {offer.connector_lineage.rooms.map((room) => (
                            <SurchargeLines
                              key={room.room_index}
                              roomIndex={room.room_index}
                              surcharges={room.surcharges}
                            />
                          ))}
                        </Box>
                      </Box>
                      {offer.cancellation && openPolicies[`${offer.search_id}:${offer.fare_id}`] && (
                        <Box component="tr" sx={{ display: { xs: 'block', md: 'table-row' } }}>
                          <Box component="td" colSpan={5} sx={{ width: '100%' }}>
                            <HotelCancellationDetails policy={offer.cancellation} />
                          </Box>
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Stack>
    </Box>
  );
}
